import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

const PaymentSection = ({ packages, title = "Choose Your Celebration", subtitle = "Every package includes full private theatre access — crafted for unforgettable moments" }) => {
  const navigate = useNavigate();
  const [visibleCards, setVisibleCards] = useState(new Set());
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = entry.target.getAttribute("data-card-index");
            setVisibleCards((prev) => new Set([...prev, index]));
          }
        });
      },
      { threshold: 0.2 }
    );

    if (containerRef.current) {
      const cards = containerRef.current.querySelectorAll("[data-card-index]");
      cards.forEach((card) => observer.observe(card));
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={containerRef}
      style={{
        background: "linear-gradient(135deg, #1A0030 0%, #2D0A47 50%, #1A0030 100%)",
        padding: "60px 20px",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bodoni+Moda:wght@700&display=swap');
        
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-40px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(40px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes glow {
          0%, 100% {
            box-shadow: 0 0 20px rgba(201, 168, 76, 0.3), 0 8px 24px rgba(94, 14, 115, 0.2);
          }
          50% {
            box-shadow: 0 0 50px rgba(201, 168, 76, 0.6), 0 12px 40px rgba(94, 14, 115, 0.4);
          }
        }

        @keyframes glowIntense {
          0%, 100% {
            box-shadow: 0 0 40px rgba(201, 168, 76, 0.7), 0 12px 50px rgba(94, 14, 115, 0.5);
          }
          50% {
            box-shadow: 0 0 80px rgba(201, 168, 76, 0.9), 0 20px 60px rgba(94, 14, 115, 0.6);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-8px);
          }
        }

        .payment-container {
          max-width: 1200px;
          margin: 0 auto;
          width: 100%;
        }

        .payment-header {
          text-align: center;
          margin-bottom: 60px;
        }

        .payment-header h2 {
          font-family: 'Bodoni Moda', 'Didot', 'Bodoni MT', serif;
          font-size: clamp(28px, 4vw, 44px);
          font-weight: 700;
          color: #fff;
          margin-bottom: 12px;
          line-height: 1.3;
          letter-spacing: 1px;
          font-style: italic;
        }

        .payment-header h2 .highlight {
          color: #C9A84C;
        }

        .payment-tag {
          display: block;
          font-size: 11px;
          font-weight: 700;
          color: #C9A84C;
          text-transform: uppercase;
          letter-spacing: 3px;
          margin-bottom: 16px;
          text-align: center;
        }

        .payment-header p {
          font-size: clamp(14px, 2vw, 18px);
          color: rgba(255, 255, 255, 0.8);
          max-width: 600px;
          margin: 0 auto;
          line-height: 1.6;
        }

        .payment-grid {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 28px;
          flex-wrap: wrap;
          max-width: 1100px;
          margin: 0 auto;
        }

        .payment-card {
          border-radius: 26px;
          overflow: hidden;
          background: rgba(255, 255, 255, 0.98);
          box-shadow: 0 6px 24px rgba(94, 14, 115, 0.1);
          transition: all 0.4s cubic-bezier(0.25, 1, 0.5, 1);
          cursor: pointer;
          position: relative;
          padding: 28px;
          flex: 0 1 320px;
          display: flex;
          flex-direction: column;
          opacity: 0;
          transform: translateZ(0);
          will-change: transform, box-shadow;
        }

        .payment-card[data-visible="true"] {
          opacity: 1;
          animation: slideInUp 0.6s cubic-bezier(0.25, 1, 0.5, 1) forwards;
        }

        .payment-card[data-visible="true"][data-card-index="0"] {
          animation: slideInLeft 0.6s cubic-bezier(0.25, 1, 0.5, 1) forwards;
        }

        .payment-card[data-visible="true"][data-card-index="2"] {
          animation: slideInRight 0.6s cubic-bezier(0.25, 1, 0.5, 1) forwards;
        }

        .payment-card:hover {
          transform: translateY(-15px) scale(1.08) !important;
          box-shadow: 0 45px 90px rgba(201, 168, 76, 0.4) !important;
          animation: glowIntense 0.6s ease-in-out infinite;
        }

        .payment-card.featured {
          flex: 0 1 360px;
          transform: scale(1.05);
          box-shadow: 0 24px 56px rgba(94, 14, 115, 0.25);
          border: 2px solid #C9A84C;
          animation: glow 3s ease-in-out infinite;
        }

        .payment-card.featured:hover {
          transform: scale(1.12) translateY(-12px) !important;
          box-shadow: 0 50px 100px rgba(201, 168, 76, 0.5) !important;
          animation: glowIntense 0.6s ease-in-out infinite;
        }

        .payment-tier {
          position: absolute;
          top: 14px;
          right: 16px;
          font-family: 'Playfair Display', serif;
          font-size: 48px;
          font-weight: 700;
          line-height: 1;
          color: rgba(94, 14, 115, 0.08);
          pointer-events: none;
          user-select: none;
        }

        .payment-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 9.5px;
          font-weight: 700;
          letter-spacing: 1px;
          text-transform: uppercase;
          padding: 6px 12px;
          border-radius: 999px;
          margin-bottom: 12px;
          width: fit-content;
          background: rgba(94, 14, 115, 0.08);
          border: 1px solid rgba(94, 14, 115, 0.15);
          color: #5E0E73;
        }

        .payment-card.featured .payment-badge {
          background: linear-gradient(135deg, #B8860B, #C9A84C, #E8D5A0);
          border: none;
          color: #fff;
          box-shadow: 0 4px 12px rgba(201, 168, 76, 0.3);
        }

        .payment-name {
          font-family: 'Playfair Display', serif;
          font-size: clamp(18px, 3vw, 24px);
          font-weight: 700;
          margin: 0 0 12px;
          color: #1A0030;
        }

        .payment-price {
          font-family: 'Playfair Display', serif;
          font-size: clamp(2rem, 4vw, 2.8rem);
          font-weight: 900;
          margin: 16px 0;
          color: #C9A84C;
        }

        .payment-duration {
          font-size: 13px;
          color: #666;
          margin-bottom: 16px;
        }

        .payment-features {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 0;
        }

        .payment-feature {
          text-align: left;
          padding: 10px 0;
          font-size: 14px;
          color: #333;
          font-family: 'DM Sans', sans-serif;
          border-bottom: 1px solid rgba(94, 14, 115, 0.05);
        }

        .payment-feature:last-child {
          border-bottom: none;
        }

        .payment-btn {
          background: linear-gradient(135deg, #B8860B, #C9A84C, #E8D5A0, #C9A84C, #9A6E10);
          background-size: 200% 200%;
          animation: gradientShift 3s ease infinite;
          color: #1A0030;
          border: none;
          border-radius: 50px;
          padding: 13px 28px;
          font-weight: 800;
          font-size: 14px;
          cursor: pointer;
          box-shadow: 0 6px 20px rgba(201, 168, 76, 0.45);
          text-decoration: none;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-family: 'DM Sans', sans-serif;
          transition: transform 0.3s, box-shadow 0.3s;
          width: 100%;
          margin-top: 20px;
          position: relative;
          overflow: hidden;
        }

        .payment-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 55%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
          animation: shine 3s ease infinite;
        }

        .payment-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 32px rgba(201, 168, 76, 0.55);
        }

        @keyframes gradientShift {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }

        @keyframes shine {
          0% {
            left: -100%;
          }
          100% {
            left: 100%;
          }
        }

        @media (max-width: 768px) {
          .payment-grid {
            gap: 12px;
            flex-direction: column;
            align-items: stretch;
          }

          .payment-tag {
            font-size: 9px;
            margin-bottom: 12px;
            letter-spacing: 2px;
          }

          .payment-card {
            flex: 0 1 auto;
            flex-basis: 100%;
            padding: 18px;
          }

          .payment-card.featured {
            flex: 0 1 auto;
            transform: scale(1);
            box-shadow: 0 12px 30px rgba(94, 14, 115, 0.15);
          }

          .payment-card.featured:hover {
            transform: translateY(-6px);
            box-shadow: 0 16px 40px rgba(94, 14, 115, 0.2);
          }

          .payment-price {
            font-size: clamp(1.6rem, 3vw, 2rem);
          }

          .payment-feature {
            font-size: 12px;
            padding: 6px 0;
          }

          .payment-btn {
            font-size: 12px;
            padding: 10px 16px;
          }

          .payment-header {
            margin-bottom: 40px;
          }
        }

        @media (max-width: 640px) {
          .payment-header {
            margin-bottom: 30px;
          }

          .payment-tag {
            font-size: 8px;
            margin-bottom: 10px;
            letter-spacing: 1.5px;
          }

          .payment-header h2 {
            font-size: clamp(24px, 5vw, 32px);
          }

          .payment-card {
            padding: 14px;
          }

          .payment-tier {
            font-size: 36px;
          }

          .payment-price {
            font-size: clamp(1.3rem, 2.5vw, 1.6rem);
          }

          .payment-feature {
            font-size: 11px;
            padding: 4px 0;
          }

          .payment-btn {
            font-size: 11px;
            padding: 9px 14px;
          }
        }
      `}</style>

      <div className="payment-container">
        <div className="payment-header">
          <span className="payment-tag">✦ Party Packages ✦</span>
          <h2>{title.split(" ").map((word, idx) => (word === title.split(" ")[title.split(" ").length - 1] ? <span key={idx} className="highlight">{word}</span> : <span key={idx}>{word} </span>))}</h2>
          <p>{subtitle}</p>
        </div>

        <div className="payment-grid">
          {packages.map((pkg, idx) => (
            <div
              key={idx}
              data-card-index={idx}
              data-visible={visibleCards.has(String(idx))}
              className={`payment-card ${pkg.isFeatured ? "featured" : ""}`}
            >
              <div className="payment-tier">{pkg.tier}</div>
              <div className="payment-badge">{pkg.badge}</div>
              <h3 className="payment-name">{pkg.name}</h3>
              <div className="payment-price">{pkg.price}</div>
              <div className="payment-duration">📅 {pkg.duration}</div>
              <div className="payment-features">
                {pkg.features.map((feature, fidx) => (
                  <div key={fidx} className="payment-feature">
                    {feature}
                  </div>
                ))}
              </div>
              <button className="payment-btn">
                {pkg.isFeatured ? "Book This Plan →" : "Proceed to Book →"}
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PaymentSection;
